// Librairies
import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

// Services
import { Pop } from 'src/app/classes/shared/pop';
import { CompanyService } from 'src/app/services/api/companies/company.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { CompanyStore } from 'src/app/services/stores/company.store';

// Interfaces
import { CompanyDetail } from 'src/app/interfaces/api/company';

// Components
import { FileInputComponent } from 'projects/ui-library/src/lib/components/file-input/file-input.component';
import { UserAvatarComponent } from 'projects/ui-library/src/lib/components/user-avatar/user-avatar.component';
import { CompanyLogoComponent } from 'src/app/components/shared/company-logo/company-logo.component';
import { ButtonComponent, SpinnerComponent } from 'ui-library';
import { localSearch, MemberOption, MemberSearchComponent } from 'src/app/components/shared/member-search/member-search.component';

type MemberKind = 'projects' | 'users';

// API error field → message key.
const FIELD_ERRORS: Record<string, string> = {
  name: 'companies.errors.name_taken',
  logo: 'companies.errors.invalid_logo',
  users: 'companies.errors.user_taken',
};

@Component({
  selector: 'app-company-drawer',
  standalone: true,
  imports: [
    FormsModule, MatFormFieldModule, MatIconModule, MatInputModule, TranslateModule,
    ButtonComponent, SpinnerComponent, FileInputComponent, UserAvatarComponent, CompanyLogoComponent, MemberSearchComponent
  ],
  templateUrl: './company-drawer.component.html',
  styleUrl: './company-drawer.component.scss',
  host: { '(document:keydown.escape)': 'closed.emit()' }
})

export class CompanyDrawerComponent {
  // Services
  private readonly companyService = inject(CompanyService);
  private readonly projectService = inject(ProjectService);
  private readonly userService = inject(UserService);
  private readonly companyStore = inject(CompanyStore);
  private readonly pop = inject(Pop);
  private readonly translate = inject(TranslateService);

  // Signals
  readonly companyId = input<string | null>(null);
  readonly closed = output<void>();
  readonly saved = output<void>();

  readonly company = signal<CompanyDetail | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');

  readonly projectIds = computed(() => this.company()?.projects.map(project => project.id) ?? []);
  readonly userIds = computed(() => this.company()?.users.map(user => user.id) ?? []);

  // Variables
  name = '';
  logo: string | undefined;
  // data:image to upload, '' to remove, undefined to keep.
  private logoChange: string | undefined;

  // Every project and user loaded once (light lists), at the first search, while the drawer is open.
  readonly searchProjects = localSearch(() => this.projectService.searchAndCountProjects('', { light: true, archived: true }).pipe(
    map(({ results }) => results.map(project => ({ id: project.id, label: project.name })))
  ));

  readonly searchUsers = localSearch(() => this.userService.searchAndCountUsers('', { light: true }).pipe(
    map(({ results }) => results.map(user => ({ id: user.id, label: `${user.firstName} ${user.lastName}`, sublabel: user.email })))
  ));

  constructor() {
    effect(() => {
      const id = this.companyId();
      untracked(() => id ? this.load(id) : this.reset());
    });
  }

  save(): void {
    const name = this.name.trim();
    if (!name) return this.error.set('companies.errors.name_required');

    const current = this.company();
    const logo = this.logoChange;
    const request = current
      ? this.companyService.updateCompany({ id: current.id, name, ...(logo !== undefined && { logo }) })
      : this.companyService.addCompany({ name, ...(logo && { logo }) });

    this.saving.set(true);
    this.error.set('');
    request.subscribe({
      next: company => {
        this.saving.set(false);
        this.pop.success(this.translate.instant(current ? 'companies.saved' : 'companies.created'));
        this.companyStore.refresh(company);
        this.saved.emit();
        this.load(company.id);
      },
      error: err => {
        this.saving.set(false);
        this.error.set(this.errorKey(err));
      }
    });
  }

  attach(kind: MemberKind, option: MemberOption): void {
    const id = this.company()!.id;
    const request = kind === 'projects' ? this.companyService.attachProject(id, option.id) : this.companyService.attachUser(id, option.id);
    request.subscribe({ next: () => this.membersChanged(), error: err => this.pop.error(this.translate.instant(this.errorKey(err))) });
  }

  detach(kind: MemberKind, memberId: string, label: string): void {
    const id = this.company()!.id;
    this.pop.confirm(this.translate.instant(`companies.detach_${kind}_confirm`, { name: label }), (confirm: { ok: boolean }) => {
      if (!confirm.ok) return;
      const request = kind === 'projects' ? this.companyService.detachProject(id, memberId) : this.companyService.detachUser(id, memberId);
      request.subscribe({ next: () => this.membersChanged(), error: err => this.pop.error(this.translate.instant(this.errorKey(err))) });
    }, 'companies.detach');
  }

  onLogoSelected(files: FileList): void {
    const file = files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => this.logo = this.logoChange = reader.result as string;
    reader.readAsDataURL(file);
  }

  onLogoDeleted(): void {
    this.logo = undefined;
    this.logoChange = '';
  }

  private load(id: string): void {
    this.loading.set(true);
    this.companyService.getCompany(id).subscribe({
      next: company => {
        this.company.set(company);
        this.name = company.name;
        this.logo = company.logo;
        this.logoChange = undefined;
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.error.set(this.errorKey(err));
      }
    });
  }

  private membersChanged(): void {
    this.saved.emit();
    this.load(this.company()!.id);
  }

  private reset(): void {
    this.company.set(null);
    this.name = '';
    this.logo = this.logoChange = undefined;
    this.error.set('');
  }

  private errorKey(err: any): string {
    return FIELD_ERRORS[err?.error?.field] ?? err?.error?.error ?? 'companies.errors.generic';
  }
}
